// frontend/src/pages/BidWritingWord.tsx — Word 风格标书生成界面
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { templateApi } from '../services/templateApi';
import { uploadReferenceFile, suggestReferences, getProjectReferences, deleteReference } from '../services/ragApi';
import { toast } from '../store/toastStore';
import { OutlineItem } from '../components/wizard/useWizardStore';
import { ContentBlock } from '../types';
import { ensureUids } from '../utils/outlineUtil';
import { extractBlobError } from '../utils/errorUtils';
import { DOCUMENT_ACCEPT } from '../utils/fileTypes';
import {
  supportsSavePicker,
  pickSaveLocation,
  writeBlobToFile,
  downloadBlob,
  WORD_FILE_TYPE,
} from '../utils/saveFile';
import type { PickedFileHandle } from '../utils/saveFile';
import { useExportProgress } from '../hooks/useExportProgress';
import ExportProgressModal from '../components/export/ExportProgressModal';
import { TemplateItem } from '../types';
import TinyMCEEditor, { TinyMCEEditorHandle } from '../components/editor/TinyMCEEditor';
import { useBlockOperations } from '../components/editor/hooks/useBlockOperations';
import { useStreamingGenerate } from '../components/editor/hooks/useStreamingGenerate';
import { contentToBlocks, blocksToHtml } from '../utils/contentBlocks';
import { countWordsInText } from '../utils/wordCount';
import PreviewToolbar from '../components/preview/PreviewToolbar';
import OutlineSidebar from '../components/preview/OutlineSidebar';
import ImageReplaceModal from '../components/ImageReplaceModal';
import RegenerateModal from '../components/RegenerateModal';
import { ImageAsset } from '../services/mediaApi';

/** 分层解析：识别 1./1.1/1.1.1 编号章节标题。
 * 严格过滤：编号后必须跟中文字符（不是标点/顿号/数字），
 * 一级编号 1~20 以内，排除正文里的序号（如"1、文档管理"、"30分钟"等）。*/
const parseOutline = (content: string): Array<{ num: string; title: string; level: number; body: string }> => {
  const lines = (content || '').split('\n').map(l => l.trim()).filter(Boolean);
  // 编号 + 可选点 + 可选空格 + 中文标题（至少 2 个中文字符开头，不含顿号/括号）
  const re = /^(\d{1,2}(?:\.\d{1,2}){0,3})\.?\s*([一-鿿][一-鿿\w（）()]+.*)$/;
  const items: any[] = [];
  const seenFirstLevel = new Set<string>();
  for (const line of lines) {
    const m = line.match(re);
    if (m) {
      const num = m[1];
      const parts = num.split('.').filter(Boolean);
      const level = parts.length;
      const firstNum = parseInt(parts[0]);
      if (firstNum > 20) continue;
      if (level === 1 && seenFirstLevel.has(parts[0])) continue;
      let title = m[2].trim();
      title = title.replace(/\s+\d+$/, '').trim();
      if (/[。；，,]/.test(title)) continue;
      if (/[:：]/.test(title)) continue;
      if (title.length >= 2 && title.length <= 30) {
        if (level === 1) seenFirstLevel.add(parts[0]);
        items.push({ num, title, level, body: '' });
        continue;
      }
    }
    // 非标题行 → 累加到最后一个 item 的 body
    if (items.length > 0) {
      items[items.length - 1].body += (items[items.length - 1].body ? '\n' : '') + line;
    }
  }
  return items;
};

/** Deep-update an outline item by uid */
function updateOutlineItem(
  items: OutlineItem[],
  uid: string,
  fn: (item: OutlineItem) => OutlineItem
): OutlineItem[] {
  return items.map((item) => {
    if (item.uid === uid) return fn(item);
    if (item.children?.length) {
      return { ...item, children: updateOutlineItem(item.children, uid, fn) };
    }
    return item;
  });
}

const BidWritingWord: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // 状态
  const [outline, setOutline] = useState<OutlineItem[]>([]);
  const storageKey = `bidword_opts_${id || 'default'}`;
  const loadOpts = () => {
    try { const v = JSON.parse(localStorage.getItem(storageKey) || '{}'); return v; } catch { return {}; }
  };
  const savedOpts = loadOpts();

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(savedOpts.selectedTemplateId ?? null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState(savedOpts.templateName || '');
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [regenChapterItem, setRegenChapterItem] = useState<OutlineItem | null>(null);
  const [pendingImageSrc, setPendingImageSrc] = useState<string | null>(null);
  const [pendingImageQuery, setPendingImageQuery] = useState('');
  const [pendingSlotId, setPendingSlotId] = useState('');
  const [modelConfigId, setModelConfigId] = useState<number | null>(null);
  const [subtreeBatch, setSubtreeBatch] = useState({ active: false, current: 0, total: 0 });
  const subtreeCancelRef = useRef(false);

  // ========== 参考资料：上传 + AI 引用规划 ==========
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRefModal, setShowRefModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadChapter, setUploadChapter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [suggesting, setSuggesting] = useState(false);
  const [referenceFiles, setReferenceFiles] = useState<any[]>([]);
  const [manualRefText, setManualRefText] = useState('');
  const [manualRefTitle, setManualRefTitle] = useState('');
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<any>(null);
  const [selectedParas, setSelectedParas] = useState<Set<number>>(new Set());
  const [expandedChapters, setExpandedChapters] = useState<Set<number>>(new Set());

  // 参考资料：上传 modal 打开时拉取列表
  useEffect(() => {
    if (!showUploadModal || !id) return;
    getProjectReferences(parseInt(id))
      .then((refs) => setReferenceFiles(Array.isArray(refs) ? refs : []))
      .catch(() => setReferenceFiles([]));
  }, [showUploadModal, id]);

  // 引用规划 modal 打开时拉取（只显示用户上传的文件，过滤掉网络搜索的）
  useEffect(() => {
    if (!showRefModal || !id) return;
    getProjectReferences(parseInt(id))
      .then((refs) => {
        const all = Array.isArray(refs) ? refs : [];
        // 只保留用户上传的文件（url 以 uploads/ 开头），排除网络搜索抓取的
        setReferenceFiles(all.filter((r: any) => (r.url || '').startsWith('uploads/')));
      })
      .catch(() => setReferenceFiles([]));
  }, [showRefModal, id]);

  const [searchRefs, setSearchRefs] = useState<{ title: string; url: string }[]>([]);
  const [projectName, setProjectName] = useState('标书文档');
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('bidword_sidebar_width');
    return saved ? parseInt(saved) : 256;
  });

  // 生成选项（从 localStorage 恢复上次配置）
  const [useRAG, setUseRAG] = useState(savedOpts.useRAG || false);
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(savedOpts.selectedSkillId || null);
  const [skills, setSkills] = useState<{ id: number; name: string }[]>([]);
  // 生图引擎：none=不生图 / mermaid
  const [imageMode, setImageMode] = useState<'none' | 'mermaid'>(savedOpts.imageMode || 'mermaid');
  // 图表风格：random=随机 / default=默认 / 预设风格 id（仅 mermaid 生效，默认随机以解决千篇一律）
  const [diagramStyle, setDiagramStyle] = useState<string>(savedOpts.diagramStyle || 'random');
  const [diagramStyles, setDiagramStyles] = useState<{ id: number; name: string }[]>([]);

  // 配置变化时保存到 localStorage
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({ useRAG, selectedSkillId, selectedTemplateId, templateName, imageMode, diagramStyle }));
  }, [useRAG, selectedSkillId, selectedTemplateId, templateName, imageMode, diagramStyle, storageKey]);

  // 加载内容技能（仅 content 类别；图表风格已独立，不再混入技能）
  useEffect(() => {
    api.get('/api/skills/active', { params: { category: 'content' } }).then((res: any) => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.skills || []);
      if (Array.isArray(list)) setSkills(list.map((s: any) => ({ id: s.id, name: s.name })));
    }).catch(() => {});
    // 加载图表风格（独立表，公开接口）
    api.get('/api/diagram-styles').then((res: any) => {
      const list = Array.isArray(res.data) ? res.data : [];
      setDiagramStyles(list.map((s: any) => ({ id: s.id, name: s.name })));
    }).catch(() => {});
  }, []);

  // 加载项目数据和模板列表
  useEffect(() => {
    if (!id) return;

    // 加载项目数据
    api
      .get(`/api/projects/${id}`)
      .then((res) => {
        const raw = res.data.outline_data?.outline || [];
        setOutline(ensureUids(raw));
        const pn = (res.data && (res.data.project_name || res.data.name)) || '';
        if (pn && pn.trim()) setProjectName(pn.trim());
        if (res.data.model_config_id) {
          setModelConfigId(res.data.model_config_id);
        } else {
          // fallback: 用用户第一个模型配置
          api.get('/api/models').then((modelRes) => {
            const models = modelRes.data?.models || modelRes.data || [];
            if (Array.isArray(models) && models.length > 0) {
              setModelConfigId(models[0].id);
            }
          }).catch(() => {});
        }
      })
      .catch(() => {
        toast.error('加载项目数据失败');
      });

    // 加载模板列表（用于查找模板名称显示在状态栏）
    templateApi
      .getTemplates({ page_size: 50 })
      .then((res) => {
        setTemplates(res.templates || []);
      })
      .catch(() => {
        // 静默失败
      });
  }, [id]);

  // 恢复模板后自动加载预览
  useEffect(() => {
    if (selectedTemplateId && !previewBlob && !isLoading) {
      fetchPreview(selectedTemplateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId]);

  // 请求预览（仅用于导出校验，不再渲染 docx-preview）
  const fetchPreview = useCallback(
    async (templateId: number) => {
      if (!id) return;
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.post(
          `/api/projects/${id}/export/preview`,
          templateId ? { use_template: true, template_id: templateId } : { use_template: false },
          { responseType: 'blob' }
        );
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        setPreviewBlob(blob);
      } catch (err: any) {
        const message = await extractBlobError(err, '预览生成失败，请重试');
        setError(message);
        setPreviewBlob(null);
      } finally {
        setIsLoading(false);
      }
    },
    [id]
  );

  // 模板切换时重新请求预览，同时更新模板名称
  const handleTemplateChange = useCallback(
    (templateId: number) => {
      setSelectedTemplateId(templateId);
      const found = templates.find((t) => t.id === templateId);
      setTemplateName(found ? found.template_name : '无模板');
      fetchPreview(templateId);
    },
    [fetchPreview, templates]
  );

  // 刷新预览（仅重新生成 .docx 用于导出校验）
  const handleRefresh = useCallback(() => {
    if (selectedTemplateId) {
      fetchPreview(selectedTemplateId);
    }
  }, [selectedTemplateId, fetchPreview]);

  const exportProgress = useExportProgress();

  // 导出下载
  const handleExport = useCallback(async () => {
    if (!id) return;

    // 先弹出系统"另存为"对话框，让用户选择保存位置（不支持的浏览器回退为直接下载）
    const filename = `${projectName}.docx`;
    let fileHandle: PickedFileHandle | null = null;
    if (supportsSavePicker()) {
      try {
        fileHandle = await pickSaveLocation(filename, WORD_FILE_TYPE);
        if (!fileHandle) return; // 用户取消
      } catch {
        fileHandle = null;
      }
    }

    try {
      exportProgress.start('word', '已提交导出请求，正在生成文档...');
      const response = await api.post(
        `/api/projects/${id}/export/word`,
        selectedTemplateId ? { use_template: true, template_id: selectedTemplateId } : { use_template: false },
        { responseType: 'blob' }
      );

      exportProgress.setStage(fileHandle ? '文档生成完成，正在写入所选保存位置...' : '文档生成完成，正在准备下载...');
      const blob = new Blob([response.data], { type: WORD_FILE_TYPE.mimeType });
      if (fileHandle) {
        await writeBlobToFile(fileHandle, blob);
        exportProgress.finish(`已保存到所选位置：${fileHandle.name}`);
        toast.success(`文档已保存：${fileHandle.name}`);
      } else {
        downloadBlob(blob, filename);
        exportProgress.finish('下载已开始');
        toast.success('文档导出成功');
      }
    } catch (err: any) {
      exportProgress.cancel();
      const message = await extractBlobError(err, '导出失败，请重试');
      toast.error(message);
    }
  }, [id, selectedTemplateId, projectName, exportProgress]);

  // 展开目录树为扁平列表（包含所有层级的标题）
  const flatOutline = useMemo(() => {
    const result: OutlineItem[] = [];
    const walk = (items: OutlineItem[]) => {
      for (const item of items) {
        result.push(item);
        if (item.children && item.children.length > 0) {
          walk(item.children);
        }
      }
    };
    walk(outline);
    return result;
  }, [outline]);

  const activeItem = useMemo(
    () => flatOutline.find((o) => o.uid === activeChapterId) || null,
    [flatOutline, activeChapterId]
  );
  const activeHasContent = activeItem ? contentToBlocks(activeItem.content).length > 1 : false;
  const activeHasChildren = !!activeItem?.children?.length;

  // 收集当前节点及其全部后代中尚未生成正文的章节，保持目录顺序
  const collectPendingSubtreeItems = useCallback((node: OutlineItem): OutlineItem[] => {
    const result: OutlineItem[] = [];
    const walk = (item: OutlineItem) => {
      if (contentToBlocks(item.content).length <= 1) result.push(item);
      item.children?.forEach(child => walk(child));
    };
    walk(node);
    return result;
  }, []);

  const pendingSubtreeCount = useMemo(
    () => (activeItem ? collectPendingSubtreeItems(activeItem).length : 0),
    [activeItem, collectPendingSubtreeItems]
  );

  // outline ref for auto-save
  const outlineRef = useRef(outline);
  outlineRef.current = outline;

  // ========== 流式生成 hook ==========
  const { generateChapter, cancelGeneration, isStreaming, currentChapterUid } =
    useStreamingGenerate({
      projectId: id!,
      setOutline,
      modelConfigId,
      useRAG,
      selectedSkillId,
      imageMode,
      diagramStyle,
      onSearchRefs: setSearchRefs,
    });

  // 按选中目录范围顺序生成：选一级则包含二级/三级/四级及更深层级
  const startSubtreeGeneration = useCallback((root: OutlineItem) => {
    const pendingItems = collectPendingSubtreeItems(root);
    if (pendingItems.length === 0) {
      toast.info('该目录范围内的章节已生成完毕');
      return;
    }

    subtreeCancelRef.current = false;
    setSubtreeBatch({ active: true, current: 0, total: pendingItems.length });
    toast.info(`开始生成 ${root.id} ${root.title} 范围内 ${pendingItems.length} 个章节`);

    const runNext = (index: number) => {
      if (subtreeCancelRef.current) {
        setSubtreeBatch({ active: false, current: 0, total: 0 });
        toast.info('已停止范围生成');
        return;
      }
      if (index >= pendingItems.length) {
        setSubtreeBatch({ active: false, current: 0, total: 0 });
        toast.success('目录范围生成完成');
        return;
      }

      const item = pendingItems[index];
      setActiveChapterId(item.uid);
      setSubtreeBatch({ active: true, current: index + 1, total: pendingItems.length });
      // generateChapter 内部会捕获错误并结束当前章节，这里继续后续章节，避免一次失败中断整棵树
      generateChapter(item).finally(() => {
        if (subtreeCancelRef.current) {
          setSubtreeBatch({ active: false, current: 0, total: 0 });
          toast.info('已停止范围生成');
          return;
        }
        setTimeout(() => runNext(index + 1), 300);
      });
    };

    runNext(0);
  }, [collectPendingSubtreeItems, generateChapter]);

  const cancelSubtreeGeneration = useCallback(() => {
    subtreeCancelRef.current = true;
    cancelGeneration();
    setSubtreeBatch({ active: false, current: 0, total: 0 });
  }, [cancelGeneration]);

  // 保存 outline 到后端
  const saveOutline = useCallback(
    (data: OutlineItem[]) => {
      if (!id) return;
      api.put(`/api/projects/${id}`, { outline_data: { outline: data } }).catch(() => {});
    },
    [id]
  );

  // 流式生成完成后自动保存
  const prevIsStreamingRef = useRef(false);
  useEffect(() => {
    if (prevIsStreamingRef.current && !isStreaming) {
      saveOutline(outlineRef.current);
    }
    prevIsStreamingRef.current = isStreaming;
  }, [isStreaming, saveOutline]);

  // ========== 获取当前活动章节的 blocks ==========
  // Derive directly from outline. TipTapEditor's html !== currentHtml guard
  // prevents the edit loop — when debounced save writes same content back,
  // setContent is skipped because the HTML hasn't changed.
  const activeBlocks = useMemo(() => {
    if (!activeChapterId) return [];
    const item = flatOutline.find((o) => o.uid === activeChapterId);
    // 剥离模型思考链 <think>...</think>（含未闭合尾部），加载/显示均不展示思考内容
    const cleaned = Array.isArray(item?.content)
      ? (item!.content as ContentBlock[]).map((b) =>
          typeof b.content === 'string'
            ? { ...b, content: b.content.replace(/<think>[\s\S]*?<\/think>/g, '').replace(/<think>[\s\S]*$/g, '') }
            : b
        )
      : item?.content;
    return contentToBlocks(cleaned);
  }, [activeChapterId, flatOutline]);

  // ========== 内容变化处理 (debounced auto-save) ==========
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeChapterIdRef = useRef(activeChapterId);
  activeChapterIdRef.current = activeChapterId;
  const tipTapRef = useRef<TinyMCEEditorHandle>(null);

  const handleContentChange = useCallback(
    (_chapterUid: string, blocks: ContentBlock[]) => {
      // Debounced save to backend — does NOT update activeBlocks state
      // to avoid resetting the editor cursor via setContent
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        const uid = activeChapterIdRef.current;
        if (!uid) return;
        setOutline((prev) => {
          const next = updateOutlineItem(prev, uid, (o) => ({ ...o, content: blocks }));
          api.put(`/api/projects/${id}`, { outline_data: { outline: next } }).catch(() => {});
          return next;
        });
      }, 2000);
    },
    [id]
  );

  // ========== 块操作 hook ==========
  const {
    handleBlockAction,
    isRegenerating,
    streamingBlockId,
  } = useBlockOperations({
    projectId: id ? parseInt(id) : 0,
    blocks: activeBlocks,
    setBlocks: (newBlocks) => {
      if (activeChapterId) {
        setOutline((prev) =>
          updateOutlineItem(prev, activeChapterId, (o) => ({ ...o, content: newBlocks }))
        );
        handleContentChange(activeChapterId, newBlocks);
      }
    },
    modelConfigId,
    useRAG,
    selectedSkillId,
    onImagePickerOpen: (blockId, queryText) => {
      setPendingImageSrc(blockId);
      setPendingImageQuery(queryText);
      setPendingSlotId(blockId);
      setShowImagePicker(true);
    },
  });

  // ========== 图片选择回调 ==========
  const handleImageSelected = useCallback(
    async (image: ImageAsset) => {
      setShowImagePicker(false);
      setPendingSlotId('');
      const API_BASE = process.env.REACT_APP_API_URL ?? '';
      const imageUrl = `${API_BASE}/${image.file_path.replace(/^\//, '')}`;
      // If we have a pending slot (block-based replace), update the block
      if (pendingSlotId && activeChapterId) {
        const updated = activeBlocks.map((b) =>
          b.id === pendingSlotId
            ? { ...b, content: imageUrl, meta: { ...b.meta, queryText: pendingImageQuery || image.ai_description || '' } }
            : b
        );
        setOutline((prev) =>
          updateOutlineItem(prev, activeChapterId, (o) => ({ ...o, content: updated }))
        );
        handleContentChange(activeChapterId, updated);
      } else {
        // Insert at cursor position in editor
        tipTapRef.current?.insertImage(imageUrl, image.ai_description || '');
      }
      toast.success(pendingSlotId ? '图片已替换' : '图片已插入');
    },
    [pendingSlotId, pendingImageQuery, activeChapterId, activeBlocks, handleContentChange]
  );

  // ========== 重新生成回调 ==========
  const handleRegenConfirm = useCallback((options: { customPrompt: string; preserveExisting: boolean }) => {
    setShowRegenModal(false);
    if (regenChapterItem) {
      generateChapter(regenChapterItem, {
        customPrompt: options.customPrompt || undefined,
        preserveExisting: options.preserveExisting,
      });
      setRegenChapterItem(null);
    }
  }, [regenChapterItem, generateChapter]);

  // ========== 参考资料：AI 引用规划 ==========
  const handleSuggestReferences = useCallback(async () => {
    if (!id) return;
    const item = flatOutline.find((o) => o.uid === activeChapterId);
    if (!item) {
      toast.warning('请先选择章节');
      return;
    }
    setSuggesting(true);
    try {
      const projectId = parseInt(id);
      // 从 blocks 中提取纯文本作为 current_content
      const blocks = contentToBlocks(item.content);
      const html = blocksToHtml(blocks);
      const plainText = (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      const resp = await suggestReferences(projectId, item.id, {
        current_content: plainText,
        chapter_title: item.title || '',
        chapter_description: item.description || '',
        requirements: '',
      });
      setSuggestions(resp.suggestions || []);
      setShowRefModal(true);
    } catch (e: any) {
      toast.error('引用规划失败: ' + (e?.response?.data?.detail || e?.message || '未知错误'));
    } finally {
      setSuggesting(false);
    }
  }, [id, activeChapterId, flatOutline]);

  // ========== 参考资料：上传文件 ==========
  const handleUploadRef = useCallback(async () => {
    if (!id) return;
    if (!uploadFile) {
      toast.warning('请选择文件');
      return;
    }
    setUploading(true);
    try {
      const projectId = parseInt(id);
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('chapter_id', '');  // 不指定章节，AI 按内容自动匹配
      fd.append('title', uploadFile.name);
      await uploadReferenceFile(projectId, fd);
      toast.success('上传成功');
      setShowUploadModal(false);
      setUploadFile(null);
      // 刷新参考列表
      try {
        const refs = await getProjectReferences(projectId, uploadChapter || undefined);
        setReferenceFiles(refs || []);
      } catch {
        /* 静默失败，不阻塞主流程 */
      }
    } catch (e: any) {
      toast.error('上传失败: ' + (e?.response?.data?.detail || e?.message || '未知错误'));
    } finally {
      setUploading(false);
    }
  }, [id, uploadFile]);

  // ========== 参考资料：插入引用内容 ==========
  const handleInsertReference = useCallback(
    (s: any, mode: 'verbatim' | 'synthesize' | 'skip') => {
      if (mode === 'skip') return;
      if (!activeChapterId) {
        toast.warning('请先选择章节');
        return;
      }
      const item = flatOutline.find((o) => o.uid === activeChapterId);
      if (!item) return;
      const blocks = contentToBlocks(item.content);
      // 构造插入文本
      const quoteText = (s?.quote || '').toString();
      let insertHtml = '';
      if (mode === 'verbatim') {
        const safeQuote = quoteText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        insertHtml = `<p>${safeQuote}</p>`;
      } else {
        // 理解生成：先写入占位段，触发流式生成
        const refTitle = (s?.ref_title || '参考').toString().replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const safeQuote = quoteText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        insertHtml = `<p>[基于参考：${refTitle}] ${safeQuote}</p>`;
      }
      const newBlock: ContentBlock = {
        id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        type: 'paragraph',
        content: insertHtml,
      };
      const newBlocks = [...blocks, newBlock];
      setOutline((prev) =>
        updateOutlineItem(prev, activeChapterId, (o) => ({ ...o, content: newBlocks }))
      );
      handleContentChange(activeChapterId, newBlocks);
      toast.success(
        mode === 'verbatim' ? '已插入原段' : '已添加到章节（已记录引文，后续可生成）'
      );
      setShowRefModal(false);
    },
    [activeChapterId, flatOutline, handleContentChange]
  );

  // ========== 目录修改回调 ==========
  // 递归查找目录项
  const findItemByUid = useCallback((list: OutlineItem[], uid: string): OutlineItem | undefined => {
    for (const item of list) {
      if (item.uid === uid) return item;
      if (item.children?.length) {
        const found = findItemByUid(item.children, uid);
        if (found) return found;
      }
    }
    return undefined;
  }, []);

  const handleOutlineChange = useCallback((newOutline: OutlineItem[]) => {
    // 递归同步更新所有层级内容中的章节号
    const syncContentHeadings = (newItems: OutlineItem[]): OutlineItem[] => {
      return newItems.map((item) => {
        const existingItem = findItemByUid(outline, item.uid);
        const oldId = existingItem?.id;
        const newId = item.id;

        let updatedItem = { ...item };

        // 如果 ID 变了且有内容，更新 heading blocks
        if (oldId && newId !== oldId && item.content && Array.isArray(item.content)) {
          updatedItem = {
            ...updatedItem,
            content: item.content.map((block: any) => {
              if (block.type === 'heading') {
                // 替换旧章节号为新的
                let html = block.content || '';
                // Handle patterns like "1 标题" or "<h2>1 标题</h2>"
                const escapedOldId = oldId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`(^|>|\\s)${escapedOldId}(\\s|<)`, 'g');
                html = html.replace(regex, `$1${newId}$2`);
                return { ...block, content: html };
              }
              return block;
            }),
          };
        }

        // 递归处理子级
        if (item.children?.length) {
          updatedItem = {
            ...updatedItem,
            children: syncContentHeadings(item.children),
          };
        }

        return updatedItem;
      });
    };

    const updatedOutline = syncContentHeadings(newOutline);

    setOutline(updatedOutline);
    outlineRef.current = updatedOutline;
    if (id) {
      api.put(`/api/projects/${id}`, { outline_data: { outline: updatedOutline } }).catch(() => {});
    }
  }, [id, outline, findItemByUid]);

  // ========== 目录点击 ==========
  const handleOutlineItemClick = useCallback((uid: string) => {
    setActiveChapterId(uid);
    // 滚动到编辑器中的对应位置
    const el = document.querySelector(`[data-chapter-uid="${CSS.escape(uid)}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // 返回向导
  const handleBack = useCallback(() => {
    navigate(`/projects/${id}/wizard`);
  }, [id, navigate]);

  return (
    <div className="flex flex-col -m-7 h-[calc(100vh-56px)] overflow-hidden">
      {/* 顶部工具栏 */}
      <PreviewToolbar
        projectName={projectName}
        templates={templates}
        selectedTemplateId={selectedTemplateId}
        onTemplateChange={handleTemplateChange}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onBack={handleBack}
        isLoading={isLoading}
        skills={skills}
        selectedSkillId={selectedSkillId}
        onSkillChange={setSelectedSkillId}
        useRAG={useRAG}
        onRAGChange={setUseRAG}
        imageMode={imageMode}
        onImageModeChange={(m) => setImageMode(m)}
        diagramStyle={diagramStyle}
        onDiagramStyleChange={setDiagramStyle}
        diagramStyles={diagramStyles}
      />

      {/* 固定操作栏 */}
      <div className="shrink-0 border-b border-border bg-card sticky top-0 z-20">
        <div className="flex items-center justify-between gap-3 px-6 py-2.5">
          {/* 左侧：全部生成 + 进度 */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const pending = flatOutline.filter((o) => {
                  const blocks = contentToBlocks(o.content);
                  return blocks.length <= 1; // only heading block = empty
                });
                if (pending.length === 0) { toast.info('所有章节已生成完毕'); return; }
                toast.info(`开始生成 ${pending.length} 个章节`);
                // Generate chapters sequentially
                const next = () => {
                  const item = pending.shift();
                  if (!item) { toast.success('全部生成完成'); return; }
                  setActiveChapterId(item.uid);
                  // Use finally to ensure chain continues even if a chapter fails
                  generateChapter(item).finally(() => setTimeout(next, 300));
                };
                next();
              }}
              disabled={isStreaming || isRegenerating || subtreeBatch.active}
              className="inline-flex items-center px-3.5 py-1.5 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:border-primary hover:text-primary disabled:opacity-40 transition-all duration-150"
            >
              全部生成
            </button>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {flatOutline.filter((o) => {
                const blocks = contentToBlocks(o.content);
                return blocks.length > 1;
              }).length}/{flatOutline.length} 章节
            </span>
            {(isStreaming || subtreeBatch.active) && (
              <>
                {subtreeBatch.active && (
                  <span className="text-xs text-primary whitespace-nowrap">
                    范围生成 {subtreeBatch.current}/{subtreeBatch.total}
                  </span>
                )}
                <div className="h-2 bg-accent rounded-full overflow-hidden w-24 animate-pulse">
                  <div className="h-full bg-primary rounded-full w-2/3" />
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {(() => {
                    const item = flatOutline.find((o) => o.uid === currentChapterUid);
                    return item ? `${item.id} ${item.title}` : '';
                  })()}
                </span>
                <button
                  onClick={subtreeBatch.active ? cancelSubtreeGeneration : cancelGeneration}
                  className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 hover:border-red-300 transition-all duration-150"
                >
                  停止
                </button>
              </>
            )}
          </div>

          {/* 右侧：选中章节 + 生成按钮 */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-foreground min-w-0 truncate max-w-[300px]">
              {activeChapterId
                ? (() => { const f = flatOutline.find((o) => o.uid === activeChapterId); return f ? `${f.id} ${f.title}` : '点击左侧目录选择章节'; })()
                : '点击左侧目录选择章节'}
            </span>
            <span className="text-[10px] text-muted-foreground bg-accent px-1.5 py-0.5 rounded whitespace-nowrap">
              {[
                selectedSkillId ? skills.find((s) => s.id === selectedSkillId)?.name : '',
                useRAG ? '联网' : '',
                imageMode === 'none'
                  ? '无图'
                  : `Mermaid·${diagramStyle === 'random' ? '随机风格' : diagramStyle === 'default' ? '默认风格' : (diagramStyles.find((s) => String(s.id) === String(diagramStyle))?.name || '自定义风格')}`,
              ].filter(Boolean).join(' + ')}
            </span>
            {/* 上传参考 */}
            <button
              onClick={() => {
                // 默认填入当前章节 id
                const item = flatOutline.find((o) => o.uid === activeChapterId);
                setUploadChapter(item?.id || '');
                setUploadFile(null);
                setShowUploadModal(true);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-all duration-150"
              title="上传项目参考资料（PDF/Word/TXT/Markdown）"
            >
              上传参考
            </button>
            {/* AI 引用规划 */}
            <button
              onClick={handleSuggestReferences}
              disabled={!activeChapterId || suggesting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40 transition-all duration-150"
              title="基于当前章节内容，AI 智能推荐参考资料"
            >
              {suggesting ? '分析中...' : 'AI 引用规划'}
            </button>
            <button
              onClick={() => {
                if (!activeItem) return;
                if (activeHasContent && pendingSubtreeCount === 0) {
                  // 当前章节及子级都已有内容时，只重新生成当前章节
                  setRegenChapterItem(activeItem);
                  setShowRegenModal(true);
                  return;
                }
                startSubtreeGeneration(activeItem);
              }}
              disabled={!activeChapterId || isStreaming || isRegenerating || subtreeBatch.active}
              title={activeHasChildren ? '生成本章节及其全部下级目录' : '生成本章节'}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold bg-primary text-primary-foreground border border-primary rounded-lg hover:shadow-md hover:bg-primary/90 disabled:opacity-40 transition-all duration-150"
            >
              {isStreaming && currentChapterUid === activeChapterId
                ? '生成中...'
                : activeHasContent
                  ? (pendingSubtreeCount > 0 ? '生成下级' : '重新生成')
                  : (activeHasChildren ? '生成本级及下级' : '生成')}
            </button>
          </div>
        </div>
      </div>

      {/* 主体：目录侧栏 + 文档预览 */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* 左侧目录 — 可拖拽宽度 */}
        <div style={{ width: sidebarWidth, minWidth: 180, maxWidth: 480 }} className="shrink-0 flex flex-col overflow-hidden">
          <OutlineSidebar
            items={outline}
            activeId={activeChapterId}
            onItemClick={handleOutlineItemClick}
            onOutlineChange={handleOutlineChange}
          />
        </div>

        {/* 可拖拽分割线 */}
        <div
          className="w-1 cursor-col-resize bg-transparent hover:bg-blue-400 active:bg-blue-500 shrink-0 transition-colors relative z-10"
          onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startWidth = sidebarWidth;
            const onMove = (ev: MouseEvent) => {
              const delta = ev.clientX - startX;
              const w = Math.max(180, Math.min(480, startWidth + delta));
              setSidebarWidth(w);
              localStorage.setItem('bidword_sidebar_width', String(w));
            };
            const onUp = () => {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
        />

        {/* 中间编辑器区 */}
        <div className="flex-1 overflow-hidden bg-[#e8ecf1]">
          <TinyMCEEditor
            ref={tipTapRef}
            blocks={activeBlocks}
            activeChapterId={activeChapterId}
            onContentChange={(newBlocks) => {
              if (activeChapterId) handleContentChange(activeChapterId, newBlocks);
            }}
            onBlockAction={handleBlockAction}
            onImageInsert={() => {
              setPendingSlotId('');
              setPendingImageQuery('');
              setShowImagePicker(true);
            }}
            onImageReplace={(alt: string) => {
              setPendingSlotId('');
              setPendingImageQuery(alt);
              setShowImagePicker(true);
            }}
            isGenerating={isRegenerating || isStreaming}
          />
        </div>
      </div>

      {/* 底部统计栏 */}
      {(() => {
        let totalWords = 0;
        const walk = (items: OutlineItem[]) => {
          for (const o of items) {
            const blocks = contentToBlocks(o.content);
            for (const b of blocks) {
              totalWords += countWordsInText(b.content || '');
            }
            if (o.children) walk(o.children);
          }
        };
        walk(outline);
        const CHAR_PER_PAGE = 850;
        const pages = Math.max(1, Math.ceil(totalWords / CHAR_PER_PAGE));
        return (
          <div className="flex items-center gap-6 px-6 py-2 bg-card border-t border-border text-xs text-muted-foreground shrink-0">
            <span>模板：{templateName || '默认'}</span>
            <span className="w-px h-4 bg-border" />
            <span>总字数：{totalWords.toLocaleString()} 字</span>
            <span className="w-px h-4 bg-border" />
            <span>预估：约 {pages} 页（A4，每页约{CHAR_PER_PAGE}字）</span>
            {searchRefs.length > 0 && (
              <>
                <span className="w-px h-4 bg-border" />
                <div className="relative group">
                  <span className="text-primary cursor-pointer">
                    📚 引用 {searchRefs.length} 条资料
                  </span>
                  <div className="absolute bottom-full left-0 mb-2 w-96 max-h-64 overflow-y-auto bg-card border border-border rounded-lg shadow-modal p-3 hidden group-hover:block z-50">
                    <p className="text-[11px] font-semibold text-foreground mb-2">参考资料（仅预览，不导出）</p>
                    {searchRefs.map((r, i) => (
                      <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                        className="block text-[11px] text-primary hover:underline mb-1 leading-relaxed"
                        onClick={(e) => e.stopPropagation()}>
                        [{i + 1}] {r.title}
                      </a>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {showRegenModal && regenChapterItem && (
        <RegenerateModal
          open={showRegenModal}
          chapterTitle={`${regenChapterItem.id} ${regenChapterItem.title}`}
          hasExistingContent={
            (() => {
              const blocks = contentToBlocks(regenChapterItem.content);
              return blocks.length > 1;
            })()
          }
          onClose={() => { setShowRegenModal(false); setRegenChapterItem(null); }}
          onConfirm={handleRegenConfirm}
        />
      )}

      {showImagePicker && (
        <ImageReplaceModal
          open={showImagePicker}
          query={pendingImageQuery}
          projectId={id ? parseInt(id) : undefined}
          onSelect={handleImageSelected}
          onClose={() => { setShowImagePicker(false); setPendingImageSrc(null); }}
        />
      )}

      {/* 上传参考 Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !uploading && setShowUploadModal(false)}>
          <div
            className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-slate-800 mb-4">上传参考资料</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">选择文件</label>
                <input
                  type="file"
                  accept={DOCUMENT_ACCEPT}
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                />
                {uploadFile && (
                  <p className="text-xs text-slate-500 mt-1">
                    已选择：{uploadFile.name}（{(uploadFile.size / 1024).toFixed(1)} KB）
                  </p>
                )}
              </div>
              <div className="text-xs text-slate-500">
                📂 上传后自动存入项目资料库，AI 引用规划时按章节内容自动匹配，无需指定章节
              </div>
            </div>
            {/* 已上传参考列表 */}
            <div className="mt-4">
              <p className="text-xs font-medium text-slate-600 mb-1">
                已上传参考（{referenceFiles.length}）
              </p>
              <div className="max-h-32 overflow-y-auto border border-slate-200 rounded p-2 bg-slate-50">
                {referenceFiles.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-2">暂无</p>
                ) : (
                  <ul className="space-y-1">
                    {referenceFiles.map((r, i) => (
                      <li key={r.id ?? i} className="text-xs text-slate-700 flex items-center gap-2">
                        <span className="truncate flex-1">
                          [{i + 1}] {r.title || r.ref_title || `参考 ${r.id ?? i + 1}`}
                        </span>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`确定删除「${r.title || r.ref_title || '此参考'}」？`)) return;
                            try {
                              await deleteReference(parseInt(id!), r.id);
                              toast.success('已删除');
                              // 刷新列表
                              const refs = await getProjectReferences(parseInt(id!));
                              setReferenceFiles((Array.isArray(refs) ? refs : []).filter((rf: any) => (rf.url || '').startsWith('uploads/')));
                            } catch (e: any) {
                              toast.error('删除失败: ' + (e?.response?.data?.detail || e?.message));
                            }
                          }}
                          className="text-rose-400 hover:text-rose-600 shrink-0"
                          title="删除"
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => { if (!uploading) setShowUploadModal(false); }}
                disabled={uploading}
                className="flex-1 border border-slate-300 rounded py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={handleUploadRef}
                disabled={uploading || !uploadFile}
                className="flex-1 bg-emerald-600 text-white rounded py-2 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {uploading ? '上传中...' : '上传'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI 引用规划 Modal */}
      {showRefModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowRefModal(false)}>
          <div
            className="bg-white rounded-xl w-full max-w-3xl max-h-[80vh] overflow-y-auto p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">
                AI 引用规划（{suggestions.length} 条）
              </h3>
              <span className="text-xs text-slate-500">
                {(() => {
                  const item = flatOutline.find((o) => o.uid === activeChapterId);
                  return item ? `${item.id} ${item.title}` : '';
                })()}
              </span>
            </div>
            {suggestions.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-slate-500 text-sm">暂无推荐引用</p>
                <p className="text-slate-400 text-xs mt-2">
                  请确认该项目该章节下已上传参考资料
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {suggestions.map((s: any, i: number) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <span className="font-medium text-slate-800 text-sm flex-1">
                        {s.ref_title || s.title || `建议 ${i + 1}`}
                      </span>
                      <span
                        className={`text-[11px] px-2 py-0.5 rounded whitespace-nowrap ${
                          s.style === 'verbatim'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}
                      >
                        {s.style === 'verbatim' ? '整段引用' : '理解生成'}
                      </span>
                    </div>
                    {s.location_hint && (
                      <p className="text-xs text-slate-500 mb-1">📍 {s.location_hint}</p>
                    )}
                    {s.quote && (
                      <blockquote className="text-sm bg-white border-l-4 border-slate-300 px-3 py-2 my-2 text-slate-700 leading-relaxed">
                        {s.quote}
                      </blockquote>
                    )}
                    {s.reason && (
                      <p className="text-sm text-slate-600">💡 {s.reason}</p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleInsertReference(s, 'verbatim')}
                        className="text-xs px-3 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                      >
                        整段引用
                      </button>
                      <button
                        onClick={() => handleInsertReference(s, 'synthesize')}
                        className="text-xs px-3 py-1 bg-purple-50 text-purple-700 rounded hover:bg-purple-100"
                      >
                        理解生成
                      </button>
                      <button
                        onClick={() => handleInsertReference(s, 'skip')}
                        className="text-xs px-3 py-1 text-slate-500 hover:text-slate-700"
                      >
                        不用
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 从参考库浏览选段落 */}
            <div className="mt-6 pt-4 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">📚 浏览参考文档</h4>
              {referenceFiles.length === 0 ? (
                <p className="text-xs text-slate-400">暂无参考文件，请先上传</p>
              ) : (
                <div className="space-y-1">
                  {referenceFiles.map((rf: any) => {
                    const rid = rf.id ?? rf.ref_id;
                    return (
                      <button
                        key={rid}
                        onClick={() => {
                          setViewingDoc(rf);
                          setSelectedParas(new Set());
                          setShowDocViewer(true);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-300 border border-slate-200 rounded-lg text-left transition-colors"
                      >
                        <span className="text-sm font-medium text-slate-700 truncate flex-1">
                          📄 {rf.title || rf.ref_title || `参考 ${rid}`}
                        </span>
                        <span className="text-xs text-indigo-500 ml-2 shrink-0">
                          点击浏览 →
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 手动添加引用 */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">✏️ 手动添加引用</h4>
              <input
                type="text"
                placeholder="标题（可选，如：技术规范书 3.2节 沟通协调要求）"
                value={manualRefTitle}
                onChange={(e) => setManualRefTitle(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm mb-2"
              />
              <textarea
                placeholder="粘贴要引用的段落内容..."
                value={manualRefText}
                onChange={(e) => setManualRefText(e.target.value)}
                rows={5}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm resize-y"
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => {
                    if (!manualRefText.trim()) { toast.warning('请输入引用内容'); return; }
                    handleInsertReference({ ref_title: manualRefTitle || '手动引用', quote: manualRefText, style: 'verbatim' }, 'verbatim');
                    setManualRefText(''); setManualRefTitle('');
                  }}
                  className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  整段引用
                </button>
                <button
                  onClick={() => {
                    if (!manualRefText.trim()) { toast.warning('请输入引用内容'); return; }
                    handleInsertReference({ ref_title: manualRefTitle || '手动引用', quote: manualRefText, style: 'synthesize' }, 'synthesize');
                    setManualRefText(''); setManualRefTitle('');
                  }}
                  className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  理解生成
                </button>
              </div>
            </div>

            <button
              onClick={() => setShowRefModal(false)}
              className="mt-4 w-full border border-slate-300 rounded py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              关闭
            </button>
          </div>
        </div>
      )}

      {/* 文档浏览弹窗（多选段落引用） */}
      {showDocViewer && viewingDoc && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowDocViewer(false)}>
          <div
            className="bg-white rounded-xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 顶部栏 */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <h3 className="text-base font-bold text-slate-800 truncate max-w-md">
                  📄 {viewingDoc.title || viewingDoc.ref_title || '参考文档'}
                </h3>
                <span className="text-xs text-slate-400">
                  {selectedParas.size > 0 ? `已选 ${selectedParas.size} 段` : '勾选段落'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {selectedParas.size > 0 && (
                  <>
                    <button
                      onClick={() => {
                        const docTitle = viewingDoc.title || viewingDoc.ref_title || '参考文档';
                        const items = parseOutline(viewingDoc.content || '');
                        const selected = Array.from(selectedParas).sort((a, b) => a - b).map(i => items[i]).filter(it => it && it.body && it.body.trim());
                        const combined = selected.map(it => `${it.num} ${it.title}\n${it.body}`).join('\n\n');
                        handleInsertReference({ ref_title: docTitle, quote: combined, style: 'verbatim' }, 'verbatim');
                        setShowDocViewer(false);
                      }}
                      className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                    >
                      整段引用（{selectedParas.size}）
                    </button>
                    <button
                      onClick={() => {
                        const docTitle = viewingDoc.title || viewingDoc.ref_title || '参考文档';
                        const items = parseOutline(viewingDoc.content || '');
                        const selected = Array.from(selectedParas).sort((a, b) => a - b).map(i => items[i]).filter(it => it && it.body && it.body.trim());
                        const combined = selected.map(it => `${it.num} ${it.title}\n${it.body}`).join('\n\n');
                        handleInsertReference({ ref_title: docTitle, quote: combined, style: 'synthesize' }, 'synthesize');
                        setShowDocViewer(false);
                      }}
                      className="px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700"
                    >
                      理解生成（{selectedParas.size}）
                    </button>
                  </>
                )}
                <button onClick={() => setShowDocViewer(false)} className="text-slate-400 hover:text-slate-600 text-xl px-2">×</button>
              </div>
            </div>

            {/* 层级目录树：按编号缩进显示，点击展开内容，父子级联勾选 */}
            <div className="flex-1 overflow-y-auto p-4">
              {(() => {
                const items = parseOutline(viewingDoc.content || '');
                // 找节点 i 的子节点（紧跟 i 之后、level 更大、直到遇到 level ≤ i.level 的）
                const getChildren = (idx: number): number[] => {
                  const out: number[] = [];
                  const base = items[idx].level;
                  for (let j = idx + 1; j < items.length; j++) {
                    if (items[j].level > base) out.push(j);
                    else break;
                  }
                  return out;
                };
                return items.map((it, si) => {
                  const isExpanded = expandedChapters.has(si);
                  const checked = selectedParas.has(si);
                  const children = getChildren(si);
                  // 父级状态：已选、部分、半选
                  const allChildrenChecked = children.length > 0 && children.every(c => selectedParas.has(c));
                  const someChildrenChecked = children.some(c => selectedParas.has(c));
                  const isIndeterminate = !checked && someChildrenChecked;
                  const isFullyChecked = checked || allChildrenChecked;
                  return (
                    <div key={si} className="mb-1">
                      <div
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                          isFullyChecked ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'
                        }`}
                        style={{ paddingLeft: `${(it.level - 1) * 16 + 8}px` }}
                      >
                        <input
                          type="checkbox"
                          ref={el => { if (el) el.indeterminate = isIndeterminate; }}
                          checked={isFullyChecked}
                          onChange={() => {
                            setSelectedParas(prev => {
                              const next = new Set(prev);
                              if (isFullyChecked) {
                                // 取消当前+所有子
                                next.delete(si);
                                children.forEach(c => next.delete(c));
                              } else {
                                // 选中当前+所有子
                                next.add(si);
                                children.forEach(c => next.add(c));
                              }
                              return next;
                            });
                          }}
                          className="w-4 h-4 rounded shrink-0"
                        />
                        {it.body && (
                          <button
                            onClick={() => {
                              setExpandedChapters(prev => {
                                const next = new Set(prev);
                                if (next.has(si)) next.delete(si);
                                else next.add(si);
                                return next;
                              });
                            }}
                            className="text-xs text-indigo-500 hover:text-indigo-700 shrink-0 w-3"
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                        )}
                        <span className={`flex-1 text-sm truncate ${
                          it.level === 1 ? 'font-bold text-slate-900' :
                          it.level === 2 ? 'font-medium text-slate-800' :
                          it.level === 3 ? 'text-slate-700' : 'text-slate-600'
                        }`}>
                          <span className="text-indigo-500 text-xs mr-1.5">{it.num}</span>
                          {it.title}
                        </span>
                        {isFullyChecked && (
                          <span className="text-[10px] text-indigo-500 shrink-0">已选</span>
                        )}
                      </div>
                      {it.body && isExpanded && (
                        <div
                          className="mr-3 mt-1 mb-2 p-2 bg-slate-50 rounded text-sm text-slate-600 leading-relaxed whitespace-pre-wrap border-l-2 border-slate-200"
                          style={{ marginLeft: `${(it.level - 1) * 16 + 24}px` }}
                        >
                          {it.body}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>

            {/* 底部全选 */}
            <div className="px-6 py-2 border-t border-slate-200 flex items-center justify-between">
              <button
                onClick={() => {
                  const items = parseOutline(viewingDoc.content || '');
                  setSelectedParas(prev => {
                    const validIndices = items.map((it, i) => it.body ? i : -1).filter(i => i >= 0);
                    const allSelected = validIndices.every(i => prev.has(i));
                    return allSelected ? new Set() : new Set(validIndices);
                  });
                }}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                全选/取消全选
              </button>
              <span className="text-xs text-slate-400">{selectedParas.size} 段已选</span>
            </div>
          </div>
        </div>
      )}
      <ExportProgressModal state={exportProgress.progressState} />
    </div>
  );
};

export default BidWritingWord;
